/**
 * <responsive-video> — breakpoint-aware, deferred background video.
 *
 * Reads data-desktop-src / data-mobile-src (+ matching -poster) off the element, chooses the one
 * matching the current viewport, and only assigns the clip to the inner <video> once the element
 * nears the viewport (or immediately when data-defer is absent). The poster is chosen the same way
 * and applied straight away so only one still is ever fetched. Playback pauses when scrolled out of
 * view and when the visitor prefers reduced motion. Swaps the clip if the breakpoint changes.
 *
 * Being a custom element, it re-initialises itself on theme-editor section reloads.
 */
(() => {
  if (customElements.get('responsive-video')) return;

  class ResponsiveVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.active = false;
      this.visible = false;
      const breakpoint = parseInt(this.dataset.breakpoint, 10) || 749;
      this.mobileQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleChange = () => this.applySource();
      this.mobileQuery.addEventListener('change', this.handleChange);
      this.motionQuery.addEventListener('change', this.handleChange);

      this.applyPoster();

      const defer = this.hasAttribute('data-defer') && 'IntersectionObserver' in window;
      if (!defer) {
        this.visible = true;
        this.activate();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.visible = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.activate();
            } else {
              this.pause();
            }
          });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.handleChange);
      if (this.motionQuery) this.motionQuery.removeEventListener('change', this.handleChange);
    }

    currentSource() {
      const d = this.dataset;
      const mobile = this.mobileQuery.matches;
      return {
        src: mobile ? d.mobileSrc || d.desktopSrc : d.desktopSrc || d.mobileSrc,
        poster: mobile ? d.mobilePoster || d.desktopPoster : d.desktopPoster || d.mobilePoster,
        ratio: mobile ? d.mobileRatio || d.desktopRatio : d.desktopRatio || d.mobileRatio,
      };
    }

    applyPoster() {
      const { poster, ratio } = this.currentSource();
      if (ratio && this.video.style.aspectRatio !== ratio) this.video.style.aspectRatio = ratio;
      if (poster && this.video.getAttribute('poster') !== poster) {
        this.video.setAttribute('poster', poster);
      }
    }

    activate() {
      this.active = true;
      this.applySource();
    }

    applySource() {
      this.applyPoster();
      if (!this.active) return;
      const { src } = this.currentSource();

      if (src && this.video.getAttribute('src') !== src) {
        this.video.setAttribute('src', src);
        this.video.load();
      }
      if (this.visible) {
        this.play();
      } else {
        this.video.pause();
      }
    }

    play() {
      if (this.motionQuery.matches) {
        this.video.pause();
        return;
      }
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }

    pause() {
      if (this.active && !this.video.paused) this.video.pause();
    }
  }

  customElements.define('responsive-video', ResponsiveVideo);
})();
